import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Toast from './Toast';

const StampCardPreview = ({ shopId, shopName }) => {
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });
  const [logoUrl, setLogoUrl] = useState('');
  const [stampImageUrl, setStampImageUrl] = useState('');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#f5f5f5');
  const [backgroundType, setBackgroundType] = useState('color'); // 'color' or 'image'
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.8); // 0.1 to 1.0
  const [isUploading, setIsUploading] = useState(false);
  const [isStampUploading, setIsStampUploading] = useState(false);
  const [isBackgroundUploading, setIsBackgroundUploading] = useState(false);
  const [previewStamps, setPreviewStamps] = useState(3); // For demo purposes
  const [maxStamps, setMaxStamps] = useState(10);
  const [colorPrimary, setColorPrimary] = useState('#3A4A68');
  const [colorSecondary, setColorSecondary] = useState('#ffffff');
  const [layoutType, setLayoutType] = useState('wide'); // 'wide' or 'narrow'
  const [stampShape, setStampShape] = useState('circle'); // 'circle' or 'square'
  const [stampSize, setStampSize] = useState('medium'); // 'small', 'medium', 'large'
  const [logoType, setLogoType] = useState('square'); // 'square' or 'rectangle'
  const [logoStyle, setLogoStyle] = useState('logo-only'); // 'logo-only', 'logo-text', 'logo-text-wide'

  const db = getFirestore();
  const storage = getStorage();

  useEffect(() => {
    if (!shopId) {
      setLogoUrl('');
      setStampImageUrl('');
      setBackgroundImageUrl('');
      setBackgroundColor('#f5f5f5');
      setBackgroundType('color');
      setBackgroundOpacity(0.8);
      setMaxStamps(10);
      setColorPrimary('#3A4A68');
      setColorSecondary('#ffffff');
      setLayoutType('wide');
      setStampShape('circle');
      setStampSize('medium');
      setLogoType('square');
      setLogoStyle('logo-only');
      return;
    }
    loadShopData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const loadShopData = async () => {
    if (!shopId) return;
    try {
      const shopDoc = await getDoc(doc(db, 'shops', shopId));
      if (shopDoc.exists()) {
        const data = shopDoc.data();
        setLogoUrl(data.logoUrl || '');
        setStampImageUrl(data.stampImageUrl || '');
        setBackgroundImageUrl(data.backgroundImageUrl || '');
        setBackgroundColor(data.backgroundColor || '#f5f5f5');
        setBackgroundType(data.backgroundType || 'color');
        setBackgroundOpacity(data.backgroundOpacity || 0.8);
        setMaxStamps(data.maxStamps || data.stampsRequired || 10);
        setColorPrimary(data.colorPrimary || '#3A4A68');
        setColorSecondary(data.colorSecondary || '#ffffff');
        setLayoutType(data.layoutType || 'wide');
        setStampShape(data.stampShape || 'circle');
        setStampSize(data.stampSize || 'medium');
        setLogoType(data.logoType || 'square');
        setLogoStyle(data.logoStyle || 'logo-only');
      }
    } catch (error) {
      console.error('Error loading shop data:', error);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!shopId) {
      showToast('Geen geldige winkel gevonden. Uploaden niet mogelijk.', "error");
      setIsUploading(false);
      return;
    }

    // File size validation (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showToast('Bestand is te groot. Maximum grootte is 2MB.', "error");
      setIsUploading(false);
      return;
    }

    // File type validation
    if (!file.type.startsWith('image/')) {
      showToast('Alleen afbeeldingsbestanden zijn toegestaan.', "error");
      setIsUploading(false);
      return;
    }

    setIsUploading(true);
    try {
      // Create a unique filename with timestamp
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `logo_${timestamp}.${fileExtension}`;
      
      // Try using a different approach - upload to photos collection instead
      const storageRef = ref(storage, `photos/${shopId}/${uniqueFileName}`);
      
      // Try to upload with metadata to avoid CORS issues
      const metadata = {
        contentType: file.type,
        cacheControl: 'public, max-age=31536000',
      };
      
      await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(storageRef);
      // Update Firestore
      await updateDoc(doc(db, 'shops', shopId), {
        logoUrl: downloadURL
      });
      setLogoUrl(downloadURL);
      await loadShopData();
      showToast('Winkel logo succesvol geüpload!', "success");
    } catch (error) {
      console.error('LOGO UPLOAD ERROR:', error, 'shopId:', shopId);
      showToast('Fout bij uploaden winkel logo: ' + error.message, "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleStampImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!shopId) {
      showToast('Geen geldige winkel gevonden. Uploaden niet mogelijk.', "error");
      setIsStampUploading(false);
      return;
    }

    // File size validation (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showToast('Bestand is te groot. Maximum grootte is 2MB.', "error");
      setIsStampUploading(false);
      return;
    }

    // File type validation
    if (!file.type.startsWith('image/')) {
      showToast('Alleen afbeeldingsbestanden zijn toegestaan.', "error");
      setIsStampUploading(false);
      return;
    }

    setIsStampUploading(true);
    try {
      // Create a unique filename with timestamp
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `stamp_${timestamp}.${fileExtension}`;
      
      // Upload to photos collection
      const storageRef = ref(storage, `photos/${shopId}/${uniqueFileName}`);
      
      // Upload with metadata
      const metadata = {
        contentType: file.type,
        cacheControl: 'public, max-age=31536000',
      };
      
      await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(storageRef);
      // Update Firestore
      await updateDoc(doc(db, 'shops', shopId), {
        stampImageUrl: downloadURL
      });
      setStampImageUrl(downloadURL);
      await loadShopData();
      showToast('Zegel afbeelding succesvol geüpload!', "success");
    } catch (error) {
      console.error('STAMP UPLOAD ERROR:', error, 'shopId:', shopId);
      showToast('Fout bij uploaden zegel afbeelding: ' + error.message, "error");
    } finally {
      setIsStampUploading(false);
    }
  };

  const handleBackgroundImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!shopId) {
      showToast('Geen geldige winkel gevonden. Uploaden niet mogelijk.', "error");
      setIsBackgroundUploading(false);
      return;
    }

    // File size validation (max 5MB for background images)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Bestand is te groot. Maximum grootte is 5MB.', "error");
      setIsBackgroundUploading(false);
      return;
    }

    // File type validation
    if (!file.type.startsWith('image/')) {
      showToast('Alleen afbeeldingsbestanden zijn toegestaan.', "error");
      setIsBackgroundUploading(false);
      return;
    }

    setIsBackgroundUploading(true);
    try {
      // Create a unique filename with timestamp
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `background_${timestamp}.${fileExtension}`;
      
      // Upload to photos collection
      const storageRef = ref(storage, `photos/${shopId}/${uniqueFileName}`);
      
      // Upload with metadata
      const metadata = {
        contentType: file.type,
        cacheControl: 'public, max-age=31536000',
      };
      
      await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(storageRef);
      // Update Firestore
      await updateDoc(doc(db, 'shops', shopId), {
        backgroundImageUrl: downloadURL
      });
      setBackgroundImageUrl(downloadURL);
      await loadShopData();
      showToast('Achtergrond afbeelding succesvol geüpload!', "success");
    } catch (error) {
      console.error('BACKGROUND UPLOAD ERROR:', error, 'shopId:', shopId);
      showToast('Fout bij uploaden achtergrond afbeelding: ' + error.message, "error");
    } finally {
      setIsBackgroundUploading(false);
    }
  };

  const handleMaxStampsChange = async (value) => {
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        maxStamps: parseInt(value),
        stampsRequired: parseInt(value)
      });
      setMaxStamps(parseInt(value));
    } catch (error) {
      console.error('Error updating max stamps:', error);
    }
  };

  const handleColorChange = async (colorType, value) => {
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        [colorType]: value
      });
      if (colorType === 'colorPrimary') {
        setColorPrimary(value);
      } else if (colorType === 'colorSecondary') {
        setColorSecondary(value);
      }
    } catch (error) {
      console.error('Error updating color:', error);
    }
  };

  const handleLayoutChange = async (value) => {
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        layoutType: value
      });
      setLayoutType(value);
    } catch (error) {
      console.error('Error updating layout:', error);
    }
  };

  const handleStampShapeChange = async (value) => {
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        stampShape: value
      });
      setStampShape(value);
    } catch (error) {
      console.error('Error updating stamp shape:', error);
    }
  };

  const handleLogoTypeChange = async (value) => {
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        logoType: value
      });
      setLogoType(value);
    } catch (error) {
      console.error('Error updating logo type:', error);
    }
  };

  const handleLogoStyleChange = async (value) => {
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        logoStyle: value
      });
      setLogoStyle(value);
    } catch (error) {
      console.error('Error updating logo style:', error);
    }
  };

  const handleBackgroundTypeChange = async (value) => {
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        backgroundType: value
      });
      setBackgroundType(value);
    } catch (error) {
      console.error('Error updating background type:', error);
    }
  };

  const handleBackgroundColorChange = async (value) => {
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        backgroundColor: value
      });
      setBackgroundColor(value);
    } catch (error) {
      console.error('Error updating background color:', error);
    }
  };

  const handleBackgroundOpacityChange = async (value) => {
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        backgroundOpacity: value
      });
      setBackgroundOpacity(value);
    } catch (error) {
      console.error('Error updating background opacity:', error);
    }
  };

  const getStampSizePx = () => {
    switch (stampSize) {
      case 'small': return 35;
      case 'large': return 55;
      default: return 40; // Changed from 45 to 40
    }
  };

  const getStampImageSize = () => {
    switch (stampSize) {
      case 'small': return 15;
      case 'large': return 25;
      default: return 20;
    }
  };

  const renderStampSlot = (index, isFilled) => {
    const stampSizePx = getStampSizePx();
    const stampImageSize = getStampImageSize();
    const borderRadius = stampShape === 'circle' ? '50%' : '8px';
    
    return (
      <div
        key={index}
        className={`stamp-slot ${isFilled ? 'filled' : 'empty'}`}
        style={{
          width: `${stampSizePx}px`,
          height: `${stampSizePx}px`,
          borderRadius: borderRadius,
          border: `2px solid ${isFilled ? colorPrimary : '#ccc'}`,
          backgroundColor: isFilled 
            ? colorPrimary 
            : 'rgba(128, 128, 128, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '4px',
          position: 'relative',
          boxShadow: isFilled ? `0 2px 4px ${colorPrimary}40` : 'none'
        }}
      >
        {isFilled ? (
          <div style={{
            width: `${stampSizePx - 15}px`,
            height: `${stampSizePx - 15}px`,
            borderRadius: stampShape === 'circle' ? '50%' : '6px',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
            {stampImageUrl ? (
              <img
                src={stampImageUrl}
                alt="Stempel"
                style={{
                  width: `${stampImageSize}px`,
                  height: `${stampImageSize}px`,
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              style={{
                display: stampImageUrl ? 'none' : 'flex',
                width: `${stampImageSize}px`,
                height: `${stampImageSize}px`,
                borderRadius: stampShape === 'circle' ? '50%' : '6px',
                backgroundColor: colorPrimary,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <span style={{ color: 'white', fontSize: `${stampImageSize * 0.6}px` }}>☕</span>
            </div>
          </div>
        ) : (
          <span style={{
            fontSize: `${stampSizePx * 0.3}px`,
            color: '#666',
            fontWeight: 'bold'
          }}>
            {index + 1}
          </span>
        )}
      </div>
    );
  };

  const renderStampGrid = () => {
    // Calculate number of rows needed based on layout
    const stampsPerRow = layoutType === 'wide' ? 5 : 3;
    const numberOfRows = Math.ceil(maxStamps / stampsPerRow);
    
    const rows = [];
    for (let rowIndex = 0; rowIndex < numberOfRows; rowIndex++) {
      const startIndex = rowIndex * stampsPerRow;
      const endIndex = Math.min(startIndex + stampsPerRow, maxStamps);
      const stampsInThisRow = endIndex - startIndex;
      
      const rowStamps = [];
      for (let colIndex = 0; colIndex < stampsInThisRow; colIndex++) {
        const stampIndex = startIndex + colIndex;
        rowStamps.push(renderStampSlot(stampIndex, stampIndex < previewStamps));
      }
      
      rows.push(
        <div key={rowIndex} style={{
          display: 'flex',
          justifyContent: 'space-evenly',
          marginBottom: rowIndex < numberOfRows - 1 ? '12px' : '0'
        }}>
          {rowStamps}
        </div>
      );
    }

    return (
      <div style={{
        padding: '16px',
        backgroundColor: colorSecondary,
        borderRadius: '12px',
        border: '1px solid #e0e0e0'
      }}>
        {rows}
      </div>
    );
  };

  if (!shopId) {
    return (
      <div className="stamp-card-preview" style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        marginBottom: '24px',
        color: 'red',
        textAlign: 'center'
      }}>
        <h3>Geen winkel geselecteerd</h3>
        <p>Er is geen geldige winkel gevonden. Probeer opnieuw of neem contact op met support.</p>
      </div>
    );
  }

  return (
    <>
    <div className="stamp-card-preview" style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      marginBottom: '24px'
    }}>
      <h3 style={{
        color: colorPrimary,
        marginBottom: '20px',
        fontSize: '20px',
        fontWeight: 'bold'
      }}>
        📱 Zegelkaart Designer
      </h3>

      {/* Background Configuration */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: colorPrimary, marginBottom: '12px' }}>Achtergrond Configuratie</h4>
        
        {/* Background Type Selection */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
            Achtergrond Type
          </label>
          <select
            value={backgroundType}
            onChange={(e) => handleBackgroundTypeChange(e.target.value)}
            style={{
              width: '200px',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              fontSize: '14px'
            }}
          >
            <option value="color">Achtergrond Kleur</option>
            <option value="image">Achtergrond Afbeelding</option>
          </select>
        </div>

        {/* Background Color Section */}
        {backgroundType === 'color' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
              Achtergrond Kleur
            </label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
              Kies een kleur voor de achtergrond van de stempelkaart
            </p>
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => handleBackgroundColorChange(e.target.value)}
              style={{
                width: '100px',
                height: '40px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            />
          </div>
        )}

        {/* Background Image Section */}
        {backgroundType === 'image' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
              Achtergrond Afbeelding
            </label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
              Deze afbeelding wordt gebruikt als achtergrond voor de stempelkaart
            </p>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                width: '80px',
                height: '60px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                backgroundColor: '#f5f5f5',
                backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
                {backgroundImageUrl ? (
                  <img
                    src={backgroundImageUrl}
                    alt="Background"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <span style={{ fontSize: '16px', color: '#999' }}>🎨</span>
                )}
              </div>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBackgroundImageUpload}
                  disabled={isBackgroundUploading}
                  style={{ marginBottom: '8px' }}
                />
                {isBackgroundUploading && <span style={{ color: '#666' }}>Uploading...</span>}
              </div>
            </div>
            
            {/* Background Opacity Slider */}
            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                Doorschijnbaarheid: {Math.round(backgroundOpacity * 100)}%
              </label>
              <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                Pas de doorschijnbaarheid van de achtergrond afbeelding aan
              </p>
              <input
                type="range"
                min="0.1"
                max="1.0"
                step="0.1"
                value={backgroundOpacity}
                onChange={(e) => handleBackgroundOpacityChange(parseFloat(e.target.value))}
                style={{ width: '200px' }}
              />
              <div style={{ 
                marginTop: '8px', 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#666'
              }}>
                <span>10%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Logo Configuration */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: colorPrimary, marginBottom: '12px' }}>Logo Configuratie</h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
              Logo Type
            </label>
            <select
              value={logoType}
              onChange={(e) => handleLogoTypeChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            >
              <option value="square">Vierkant Logo (40x40)</option>
              <option value="rectangle">Breed Logo (160x80)</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
              Logo Stijl
            </label>
            <select
              value={logoStyle}
              onChange={(e) => handleLogoStyleChange(e.target.value)}
              disabled={logoType === 'rectangle'}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '14px',
                opacity: logoType === 'rectangle' ? 0.5 : 1
              }}
            >
              <option value="logo-only">Alleen Logo</option>
              <option value="logo-text">Logo + Tekst</option>
              <option value="logo-text-wide">Logo + Tekst (Breed)</option>
            </select>
            {logoType === 'rectangle' && (
              <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Breed logo toont alleen het logo
              </p>
            )}
          </div>
        </div>
        
        {/* Logo Upload Section */}
        <div style={{ marginBottom: '12px' }}>
          <h5 style={{ color: colorPrimary, marginBottom: '8px' }}>Winkel Logo Upload</h5>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Upload een {logoType === 'square' ? 'vierkant (40x40)' : 'breed (160x80)'} logo voor de header
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              width: logoType === 'square' ? '60px' : '120px',
              height: logoType === 'square' ? '60px' : '60px',
              borderRadius: logoType === 'square' ? '8px' : '0px',
              border: '2px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              backgroundColor: '#f5f5f5'
            }}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Shop Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <span style={{ fontSize: '24px' }}>🏪</span>
              )}
            </div>
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={isUploading}
                style={{ marginBottom: '8px' }}
              />
              {isUploading && <span style={{ color: '#666' }}>Uploading...</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Layout Configuration */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: colorPrimary, marginBottom: '12px' }}>Layout & Design</h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
              Layout Type
            </label>
            <select
              value={layoutType}
              onChange={(e) => handleLayoutChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            >
              <option value="wide">Breed (5 zegels per rij)</option>
              <option value="narrow">Smal (3 zegels per rij)</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
              Zegel Vorm
            </label>
            <select
              value={stampShape}
              onChange={(e) => handleStampShapeChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            >
              <option value="circle">Rond</option>
              <option value="square">Vierkant</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: '12px' }}>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Zegels zijn altijd 40x40 pixels
          </p>
        </div>
      </div>

      {/* Stamp Image Upload Section */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: colorPrimary, marginBottom: '12px' }}>Zegel Afbeelding (voor zegels)</h4>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
          Deze afbeelding wordt getoond in de gevulde zegels op de zegelkaart
        </p>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            border: '2px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: '#f5f5f5'
          }}>
            {stampImageUrl ? (
              <img
                src={stampImageUrl}
                alt="Stempel"
                style={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <span style={{ fontSize: '24px' }}>☕</span>
            )}
          </div>
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleStampImageUpload}
              disabled={isStampUploading}
              style={{ marginBottom: '8px' }}
            />
            {isStampUploading && <span style={{ color: '#666' }}>Uploading...</span>}
          </div>
        </div>
      </div>

      {/* Color Configuration */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: colorPrimary, marginBottom: '12px' }}>Winkel Kleuren</h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
              Primaire kleur
            </label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
              Gebruikt voor: shop header, stamp slots, progress bar, tekst
            </p>
            <input
              type="color"
              value={colorPrimary}
              onChange={(e) => handleColorChange('colorPrimary', e.target.value)}
              style={{
                width: '100%',
                height: '40px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
              Secundaire kleur
            </label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
              Gebruikt voor: achtergronden, accenten, contrast
            </p>
            <input
              type="color"
              value={colorSecondary}
              onChange={(e) => handleColorChange('colorSecondary', e.target.value)}
              style={{
                width: '100%',
                height: '40px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            />
          </div>
        </div>
      </div>

      {/* Max Stamps Configuration */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: colorPrimary, marginBottom: '12px' }}>Aantal zegels voor gratis koffie</h4>
        <select
          value={maxStamps}
          onChange={(e) => handleMaxStampsChange(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            fontSize: '14px'
          }}
        >
          <option value={5}>5 zegels</option>
          <option value={8}>8 zegels</option>
          <option value={10}>10 zegels</option>
          <option value={12}>12 zegels</option>
          <option value={15}>15 zegels</option>
        </select>
      </div>

      {/* Preview Controls */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: colorPrimary, marginBottom: '12px' }}>Preview zegels</h4>
        <input
          type="range"
          min="0"
          max={maxStamps}
          value={previewStamps}
          onChange={(e) => setPreviewStamps(parseInt(e.target.value))}
          style={{ width: '200px' }}
        />
        <span style={{ marginLeft: '12px', color: '#666' }}>
          {previewStamps}/{maxStamps} zegels
        </span>
      </div>

      {/* Mobile Preview - Exact zoals Flutter app */}
      <div style={{
        border: '2px solid #e0e0e0',
        borderRadius: '20px',
        padding: '20px',
        backgroundColor: colorSecondary,
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        <div style={{
          backgroundColor: backgroundType === 'color' ? backgroundColor : 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          backgroundImage: backgroundType === 'image' && backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative'
        }}>
          {/* Overlay for better text readability */}
          {backgroundType === 'image' && backgroundImageUrl && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: `rgba(255, 255, 255, ${1 - backgroundOpacity})`,
              borderRadius: '16px'
            }} />
          )}
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Shop Header - Exact zoals Flutter app */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                width: logoType === 'square' ? '40px' : '160px',
                height: logoType === 'square' ? '40px' : '80px',
                borderRadius: logoType === 'square' ? '8px' : '0px',
                backgroundColor: colorPrimary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                overflow: 'hidden'
              }}>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Shop Logo"
                    style={{
                      width: logoType === 'square' ? '24px' : '140px',
                      height: logoType === 'square' ? '24px' : '60px',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <span style={{ color: 'white', fontSize: '16px' }}>☕</span>
                )}
              </div>
              {logoStyle === 'logo-text' && logoType !== 'rectangle' && (
                <span style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: colorPrimary
                }}>
                  {shopName}
                </span>
              )}
              {logoStyle === 'logo-text-wide' && logoType !== 'rectangle' && (
                <span style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: colorPrimary,
                  flex: 1,
                  textAlign: 'center'
                }}>
                  {shopName}
                </span>
              )}
            </div>

            {/* Stamp Grid - Exact zoals Flutter app */}
            {renderStampGrid()}

            {/* Progress Bar - Exact zoals Flutter app */}
            <div style={{ marginTop: '20px' }}>
              <div style={{
                height: '6px',
                backgroundColor: '#e0e0e0',
                borderRadius: '3px',
                overflow: 'hidden',
                marginBottom: '10px'
              }}>
                <div style={{
                  width: `${(previewStamps / maxStamps) * 100}%`,
                  height: '100%',
                  backgroundColor: '#000000',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#666',
                fontWeight: '500'
              }}>
                <span>{previewStamps} van {maxStamps} stempels</span>
                <span style={{ fontWeight: 'bold' }}>{Math.round((previewStamps / maxStamps) * 100)}%</span>
              </div>
            </div>

            {/* Completion Message - Exact zoals Flutter app */}
            {previewStamps >= maxStamps && (
              <div style={{
                marginTop: '12px',
                padding: '8px 16px',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>🎉</span>
                <span style={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#4caf50'
                }}>
                  Gratis koffie beschikbaar!
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#e3f2fd',
        borderRadius: '8px',
        border: '1px solid #bbdefb'
      }}>
        <h5 style={{ color: '#1976d2', marginBottom: '8px' }}>💡 Design Tips:</h5>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#1976d2' }}>
          <li><strong>Achtergrond:</strong> Kies tussen een kleur of afbeelding voor de achtergrond</li>
          <li><strong>Doorschijnbaarheid:</strong> Pas de doorschijnbaarheid van achtergrond afbeeldingen aan</li>
          <li><strong>Logo Type:</strong> Vierkant (40x40) voor compacte weergave, Breed (160x80) voor meer ruimte</li>
          <li><strong>Logo Stijl:</strong> Alleen logo, logo + tekst, of logo + tekst (breed) - niet beschikbaar bij breed logo</li>
          <li><strong>Layout:</strong> Kies tussen breed (5 zegels per rij) of smal (3 zegels per rij)</li>
                      <li><strong>Zegel Vorm:</strong> Rond voor een zachte uitstraling, vierkant voor een moderne look</li>
            <li><strong>Zegel Grootte:</strong> Altijd 40x40 pixels</li>
          <li><strong>Winkel Logo:</strong> Upload een {logoType === 'square' ? 'vierkant (40x40)' : 'breed (160x80)'} logo</li>
          <li><strong>Zegel Afbeelding:</strong> Upload een afbeelding die in de gevulde zegels wordt getoond</li>
          <li>Beide afbeeldingen worden automatisch geschaald en gecropped</li>
          <li>Klanten zien deze zegelkaart in de mobiele app</li>
          <li>Pas het aantal zegels aan naar jouw business model</li>
          <li>De kleuren worden direct toegepast op de voorvertoning</li>
        </ul>
      </div>
    </div>
    <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default StampCardPreview;